import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Text,
  Textarea,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { useMemo, useState } from 'react'
import { FiEdit, FiSave } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { DataTable } from '@/components/common/DataTable'
import type { ActionButton, ColumnConfig } from '@/components/common/DataTable'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import { CacheRefreshIconButton, RefreshIconButton } from '@/components/common/RefreshIconButton'
import { useToast } from '@/hooks/useToast'
import {
  useRefreshSystemPlatformWalletsCache,
  useSystemPlatformWallets,
  useUpdateSystemPlatformWallet,
} from '@/hooks/useSystemConfigData'
import type { SystemPlatformWallet } from '@/services/admin/systemService'

type PlatformWalletsTabProps = {
  cardBg: string
}

type PlatformWalletFormState = {
  address: string
  isActive: boolean
  notes: string
}

const toDateLabel = (value?: string): string => {
  if (!value) return '-'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleString()
}

export function PlatformWalletsTab(props: PlatformWalletsTabProps) {
  const { t } = useTranslation()
  const toast = useToast()
  const { data, isFetching, refetch } = useSystemPlatformWallets()
  const updateMutation = useUpdateSystemPlatformWallet()
  const refreshMutation = useRefreshSystemPlatformWalletsCache()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [editingRow, setEditingRow] = useState<SystemPlatformWallet | null>(null)
  const [filters, setFilters] = useState({
    search: '',
    compatibility: 'all',
    status: 'all' as 'all' | 'active' | 'inactive',
    limit: 100,
  })
  const [form, setForm] = useState<PlatformWalletFormState>({
    address: '',
    isActive: true,
    notes: '',
  })

  const rows = data ?? []
  const compatibilityOptions = useMemo(
    () => [
      { value: 'all', label: t('common.all') },
      ...Array.from(new Set(rows.map(row => row.compatibility))).sort().map(compatibility => ({
        value: compatibility,
        label: compatibility,
      })),
    ],
    [rows, t]
  )
  const statusOptions = useMemo(
    () => [
      { value: 'all', label: t('common.all') },
      { value: 'active', label: t('common.active') },
      { value: 'inactive', label: t('common.inactive') },
    ],
    [t]
  )
  const filteredRows = useMemo(() => {
    const needle = filters.search.trim().toLowerCase()
    return rows
      .filter(row => filters.compatibility === 'all' || row.compatibility === filters.compatibility)
      .filter(row => {
        if (filters.status === 'all') return true
        return filters.status === 'active' ? row.isActive : !row.isActive
      })
      .filter(row => {
        if (!needle) return true
        return (
          row.compatibility.toLowerCase().includes(needle)
          || row.role.toLowerCase().includes(needle)
          || row.address.toLowerCase().includes(needle)
          || (row.notes ?? '').toLowerCase().includes(needle)
        )
      })
      .slice(0, filters.limit)
  }, [filters, rows])

  const columns = useMemo<ColumnConfig<SystemPlatformWallet>[]>(() => ([
    {
      key: 'compatibility',
      title: t('systemConfig.platformWallets.columns.compatibility', { defaultValue: 'Compatibility' }),
      width: '12%',
      render: (_, row) => row.compatibility,
    },
    {
      key: 'role',
      title: t('systemConfig.platformWallets.columns.role', { defaultValue: 'Role' }),
      width: '12%',
      render: (_, row) => row.role,
    },
    {
      key: 'address',
      title: t('systemConfig.platformWallets.columns.address', { defaultValue: 'Address' }),
      render: (_, row) => (
        <Text noOfLines={1} title={row.address}>
          {row.address}
        </Text>
      ),
    },
    {
      key: 'isActive',
      title: t('common.status'),
      width: '10%',
      align: 'center',
      render: (_, row) => (
        <Badge colorScheme={row.isActive ? 'green' : 'gray'}>
          {row.isActive ? t('common.active') : t('common.inactive')}
        </Badge>
      ),
    },
    {
      key: 'notes',
      title: t('systemConfig.platformWallets.columns.notes', { defaultValue: 'Notes' }),
      width: '18%',
      render: (_, row) => (
        <Text noOfLines={2} title={row.notes ?? '-'}>
          {row.notes ?? '-'}
        </Text>
      ),
    },
    {
      key: 'updatedAt',
      title: t('common.updatedAt', { defaultValue: 'Updated at' }),
      width: '14%',
      render: (_, row) => toDateLabel(row.updatedAt),
      noWrap: true,
    },
  ]), [t])

  const actions = useMemo(() => {
    return (row: SystemPlatformWallet): ActionButton[] => [
      {
        label: t('common.edit'),
        icon: <FiEdit />,
        onClick: () => {
          setEditingRow(row)
          setForm({
            address: row.address,
            isActive: row.isActive,
            notes: row.notes ?? '',
          })
          onOpen()
        },
      },
    ]
  }, [onOpen, t])

  const handleSave = async () => {
    if (!editingRow) {
      return
    }
    try {
      await updateMutation.mutateAsync({
        id: editingRow.id,
        payload: {
          address: form.address.trim(),
          isActive: form.isActive,
          notes: form.notes.trim() ? form.notes.trim() : null,
        },
      })
      toast.success({
        title: t('common.saveSuccess', { defaultValue: 'Saved' }),
      })
      onClose()
      await refetch()
    } catch (error) {
      toast.error({
        title: t('common.saveFailed', { defaultValue: 'Save failed' }),
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  const handleRefreshCache = async () => {
    try {
      await refreshMutation.mutateAsync()
      toast.success({
        title: t('systemConfig.actions.refreshCacheSuccess', { defaultValue: 'Cache refreshed' }),
      })
    } catch (error) {
      toast.error({
        title: t('systemConfig.actions.refreshCacheFailed', { defaultValue: 'Cache refresh failed' }),
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  return (
    <>
      <Card bg={props.cardBg}>
        <CardHeader>
          <Flex
            direction={{ base: 'column', xl: 'row' }}
            align={{ base: 'stretch', xl: 'end' }}
            justify="space-between"
            gap={3}
          >
            <HStack spacing={3} align="end" flexWrap={{ base: 'wrap', xl: 'nowrap' }} flex="1" minW={0}>
              <Heading size="sm" minW="fit-content">
                {t('systemConfig.tabs.platformWallets', { defaultValue: 'platform wallets' })}
              </Heading>
              <FormControl minW={{ base: '100%', md: '220px' }} maxW={{ xl: '220px' }}>
                <FormLabel fontSize="xs" mb={1}>
                  {t('common.search', { defaultValue: 'Search' })}
                </FormLabel>
                <Input
                  size="xs"
                  value={filters.search}
                  onChange={event => setFilters(prev => ({ ...prev, search: event.target.value }))}
                  placeholder={t('common.search', { defaultValue: 'Search' })}
                />
              </FormControl>
              <FormControl minW={{ base: '100%', md: '130px' }} maxW={{ xl: '130px' }}>
                <FormLabel fontSize="xs" mb={1}>
                  {t('systemConfig.platformWallets.columns.compatibility', { defaultValue: 'Compatibility' })}
                </FormLabel>
                <DropdownSelect
                  value={filters.compatibility}
                  options={compatibilityOptions}
                  onChange={value => setFilters(prev => ({ ...prev, compatibility: value }))}
                  size="xs"
                />
              </FormControl>
              <FormControl minW={{ base: '100%', md: '120px' }} maxW={{ xl: '120px' }}>
                <FormLabel fontSize="xs" mb={1}>
                  {t('common.status')}
                </FormLabel>
                <DropdownSelect
                  value={filters.status}
                  options={statusOptions}
                  onChange={value =>
                    setFilters(prev => ({ ...prev, status: value as typeof filters.status }))
                  }
                  size="xs"
                />
              </FormControl>
              <FormControl minW={{ base: '100%', md: '100px' }} maxW={{ xl: '100px' }}>
                <FormLabel fontSize="xs" mb={1}>{t('common.limit')}</FormLabel>
                <DropdownSelect
                  value={String(filters.limit)}
                  options={[50, 100, 200, 500].map(limit => ({
                    value: String(limit),
                    label: String(limit),
                  }))}
                  onChange={value => setFilters(prev => ({ ...prev, limit: Number(value) }))}
                  size="xs"
                />
              </FormControl>
            </HStack>
            <HStack spacing={2} justify={{ base: 'flex-start', xl: 'flex-end' }} flexShrink={0}>
              <RefreshIconButton
                label={t('common.refresh')}
                onClick={() => refetch()}
                isLoading={isFetching}
              />
              <CacheRefreshIconButton
                label={t('systemConfig.actions.refreshCache', { defaultValue: 'Refresh Cache' })}
                onClick={handleRefreshCache}
                isLoading={refreshMutation.isPending}
              />
            </HStack>
          </Flex>
        </CardHeader>
        <CardBody pt={0}>
          <DataTable<SystemPlatformWallet>
            data={filteredRows}
            loading={isFetching}
            columns={columns}
            actions={actions}
            showFilterBar={false}
            showRefresh={false}
            emptyMessage={t('common.noData', { defaultValue: 'No data' })}
          />
        </CardBody>
      </Card>

      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent className="system-config-dialog">
          <ModalHeader>{t('systemConfig.platformWallets.edit', { defaultValue: 'Edit platform wallet' })}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>{t('systemConfig.platformWallets.fields.address', { defaultValue: 'Address' })}</FormLabel>
                <Input
                  value={form.address}
                  onChange={event => setForm(prev => ({ ...prev, address: event.target.value }))}
                />
              </FormControl>
              <FormControl>
                <FormLabel>{t('systemConfig.platformWallets.fields.notes', { defaultValue: 'Notes' })}</FormLabel>
                <Textarea
                  value={form.notes}
                  onChange={event => setForm(prev => ({ ...prev, notes: event.target.value }))}
                />
              </FormControl>
              <FormControl display="flex" alignItems="center">
                <FormLabel mb={0}>{t('common.active')}</FormLabel>
                <Switch
                  isChecked={form.isActive}
                  onChange={event => setForm(prev => ({ ...prev, isActive: event.target.checked }))}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={2}>
              <Button variant="ghost" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button
                colorScheme="admin"
                leftIcon={<FiSave />}
                onClick={handleSave}
                isLoading={updateMutation.isPending}
              >
                {t('common.save')}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
